// The Control/Input wrapper: label above, control, then help or error below,
// at an 8px stack gap. The Figma component hides its label and help slots on
// the filter/search usage; here they are simply optional.
//
// Field owns the id so the error is genuinely announced: the control is
// cloned with `aria-describedby` pointing at the error (and help) node and
// with `aria-invalid`. A screen that renders its own <p> next to an <input>
// looks identical and tells a screen-reader user nothing, which is the
// failure this component exists to prevent.

import "./ui.css";
import { Children, cloneElement, isValidElement, useId } from "react";
import type { ReactElement, ReactNode } from "react";

type ControlAria = {
  id?: string;
  required?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

export type FieldProps = {
  label: string;
  /** Defaults to a generated id; pass one only when something else must target it. */
  id?: string;
  help?: ReactNode;
  /** Present means invalid: it sets aria-invalid and is linked by aria-describedby. */
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
};

export function Field({
  label,
  id,
  help,
  error,
  required = false,
  children,
}: FieldProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const helpId = `${controlId}-help`;
  const errorId = `${controlId}-error`;
  const describedBy = [error ? errorId : null, help ? helpId : null]
    .filter(Boolean)
    .join(" ");

  const only = Children.only(children);
  const control = isValidElement(only)
    ? cloneElement(only as ReactElement<ControlAria>, {
        id: controlId,
        required: required || undefined,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy || undefined,
      })
    : only;

  return (
    <div className="maisight-ui-field">
      <label className="maisight-ui-field-label type-label" htmlFor={controlId}>
        {label}
        {required ? (
          <span className="maisight-ui-field-required" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </label>
      {control}
      {help ? (
        <p className="maisight-ui-field-help type-bodyS" id={helpId}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p
          className="maisight-ui-field-error type-bodyS"
          id={errorId}
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
